import { acceptHMRUpdate, defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { GETShippingLine, ShippingLine } from '~/services/apiClient'
import type { LineLFDGapConfig } from '~/models/lineLFDGap'
import { buildLineLFDGapConfig } from '~/models/lineLFDGap'
import { reportWarning } from '~/observability/warnings'
import { ContainersApi } from '~/services/apiClient'

export const useShippingLinesStore = defineStore('shippingLines', () => {
  const shipping_lines = ref([] as GETShippingLine[])
  const shipping_line_lookup = ref(new Map<ShippingLine, GETShippingLine>())
  const loading = ref(false)
  function loadShippingLinesCached() {
    if (shipping_lines.value.length === 0 && !loading.value) {
      loadShippingLines()
    }
  }
  function loadShippingLines() {
    loading.value = true
    const api = new ContainersApi()
    api.getShippingLinesContainersShippingLinesGet().then((resp) => {
      // Sort so that manues and such have this pre-sorted
      shipping_lines.value = resp.data.sort((a, b) =>
        a.long_desc.localeCompare(b.long_desc)
      )
      shipping_line_lookup.value = new Map(
        resp.data.map((sl) => [sl.shipping_line, sl])
      )
      reportLinesMissingLFDGapNote(resp.data)
      loading.value = false
    })
  }

  /**
   * A line flagged as withholding its LFD but given no note has no warning to show,
   * so it is dropped from the config below and silently stops warning. Nobody can see
   * that from the UI, and the fix is in backend config rather than in the code.
   */
  function reportLinesMissingLFDGapNote(lines: GETShippingLine[]) {
    const misconfigured = lines
      .filter((line) => !line.shares_lfd_with_terminals && !line.lfd_gap_note)
      .map((line) => line.shipping_line)
    if (misconfigured.length === 0) return
    reportWarning(
      `Shipping lines withhold their LFD but have no lfd_gap_note configured, so they ` +
        `will not warn: ${misconfigured.join(', ')}`
    )
  }

  /**
   * Which lines withhold their LFD from terminals, and what to say about it.
   *
   * Empty until the lines have loaded, which is deliberate: no warning is better
   * than a wrong one, and `useContainerFilters` re-parses containers once this
   * arrives so a late load still paints the badge.
   */
  const lineLFDGapConfig = computed<LineLFDGapConfig>(() =>
    buildLineLFDGapConfig(shipping_lines.value)
  )

  function getShippingLineDescription(line: ShippingLine): string {
    const sl = shipping_line_lookup.value.get(line)
    if (sl) {
      return sl.long_desc
    }
    return line
  }

  return {
    shipping_lines,
    shipping_line_lookup,
    lineLFDGapConfig,
    loading,
    loadShippingLines,
    loadShippingLinesCached,
    getShippingLineDescription,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useShippingLinesStore, import.meta.hot)
  )
}
